import React from 'react';
import { CarouselTrack } from '../../../ui/Carousel/CarouselTrack';
import { CarouselItem } from '../../../ui/Carousel/CarouselItem';
import { CarouselIndicators } from '../../../ui/Carousel/CarouselIndicators';
import { useCarouselNavigation } from '../../../ui/Carousel/hooks/useCarouselNavigation';
import { ChevronLeftIcon } from '../../../ui/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../../../ui/icons/ChevronRightIcon';
import { FloorPlanCard } from './FloorPlanCard';
import type { Floorplan, Unit } from './UnitTable';
import * as styles from './unitsModule.styles';

type DesktopFloorPlanCarouselProps = {
  floorplans: Array<Floorplan>;
  onUnitSelect: (unit: Unit, floorplan: Floorplan) => void;
  onSeeAll: (floorplan: Floorplan) => void;
};

export const DesktopFloorPlanCarousel = ({ floorplans, onUnitSelect, onSeeAll }: DesktopFloorPlanCarouselProps) => {
  const {
    trackRef,
    canScrollLeft,
    canScrollRight,
    currentPage,
    totalPages,
    handlePrevious,
    handleNext,
    scrollToPage,
    handleScroll,
  } = useCarouselNavigation({ gap: 'sm', step: 1 });

  const showControls = totalPages > 1;

  return (
    <div>
      <div className={styles.desktopCarouselRow}>
        <div className={styles.desktopArrowColumn}>
          {showControls && (
            <button
              type="button"
              className={styles.desktopControlButton}
              onClick={handlePrevious}
              disabled={!canScrollLeft}
              aria-label="Previous floor plans"
            >
              <ChevronLeftIcon />
            </button>
          )}
        </div>

        <div className={styles.desktopCardsArea}>
          <CarouselTrack ref={trackRef} gap="sm" snapAlign="start" onScroll={handleScroll} aria-label="Floor plans">
            {floorplans.map((fp) => (
              <CarouselItem key={fp.modelId} className={styles.desktopCarouselItem}>
                <FloorPlanCard floorplan={fp} onUnitSelect={onUnitSelect} onSeeAll={onSeeAll} compact />
              </CarouselItem>
            ))}
          </CarouselTrack>
        </div>

        <div className={styles.desktopArrowColumn}>
          {showControls && (
            <button
              type="button"
              className={styles.desktopControlButton}
              onClick={handleNext}
              disabled={!canScrollRight}
              aria-label="Next floor plans"
            >
              <ChevronRightIcon />
            </button>
          )}
        </div>
      </div>

      {showControls && (
        <CarouselIndicators currentPage={currentPage} totalPages={totalPages} onPageSelect={scrollToPage} />
      )}
    </div>
  );
};
